import { useCallback, useEffect, useState } from "react";
import {
  Button,
  TimePicker,
  Form,
  Checkbox,
  Input,
  Table,
  message,
  Modal,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";

import DataTotalDisplay from "../../component/data-total-display/data-total-display.component";
import FormButtonsWrap from "../../component/form-buttons-wrap/form-buttons-wrap.component";

import { setMenu } from "../../redux/reducers/navigationSlice";
import { setUserAccount } from "../../redux/reducers/userAccountSlice";
import SchedulesAPI from "../../api/SchedulesAPI";
import { WEEKDAYS_REF, weekDaysNumbersToESText } from "../../utils/dataUtils";

import "./schedules-maintenance.styles.scss";

const schedulesFormInitialValues = {
  id: "",
  description: "",
  startTime: "",
  endTime: "",
  weekDays: [],
};

const SchedulesMaintenancePage = () => {
  const [messageApi, messageContextHolder] = message.useMessage();
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const token = useSelector((state) => state.userAccount.user.token);

  const [schedulesData, setSchedulesData] = useState({
    schedules: [],
    totalCount: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(10);
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState("");

  const schedulesTableColumns = [
    { title: "Id", dataIndex: "Id" },
    {
      title: "Descripción",
      dataIndex: "Description",
    },
    { title: "Hora Inicio", dataIndex: "StartTime" },
    {
      title: "Hora Fin",
      dataIndex: "StartTime",
    },
    { title: "Fecha Creación", dataIndex: "StartTime" },
    {
      title: "Fecha Actualización",
      dataIndex: "StartTime",
    },
    { title: "Usuario", dataIndex: "UserName" },
    {
      title: "Días",
      render: (_, { WeekDays }) => weekDaysNumbersToESText(WeekDays),
    },
    {
      title: "Opción",
      render: (_, { Id }) => (
        <FormButtonsWrap vertical>
          <Button onClick={() => loadSelectedSchedule(Id)}>Seleccionar</Button>
          <Button
            danger
            onClick={() => {
              setModalOpen(true);
              setSelectedId(Id);
            }}
          >
            Borrar
          </Button>
        </FormButtonsWrap>
      ),
    },
  ];

  const loadInitialData = useCallback(async () => {
    messageApi.open({ type: "loading", content: "Cargando..." });

    try {
      const response = await SchedulesAPI.getSchedules(token);

      if (!response.data.success) {
        messageApi.open({
          type: "error",
          content: response.data.message,
        });
        return;
      }

      setSchedulesData(response.data);
      messageApi.destroy();
    } catch (err) {
      console.error(err.message);

      let message = "Servicio no disponible, intente más tarde. ";

      if (err.code !== "ERR_NETWORK") {
        message = err.response.data.message;

        if (err.response.status === 401) dispatch(setUserAccount(null));
      }

      messageApi.open({ type: "error", content: message });
    }
  }, [token, dispatch, messageApi]);

  const loadSchedulesList = async (activePage = 1, activePageSize = 10) => {
    messageApi.open({ type: "loading", content: "Cargando..." });

    try {
      const response = await SchedulesAPI.getSchedules(token, {
        page: activePage,
        pageSize: activePageSize,
      });

      if (!response.data.success) {
        messageApi.open({
          type: "error",
          content: response.data.message,
        });
        return;
      }

      setSchedulesData(response.data);
    } catch (err) {
      console.error(err.message);

      let message = "Servicio no disponible, intente más tarde. ";

      if (err.code !== "ERR_NETWORK") {
        message = err.response.data.message;

        if (err.response.status === 401) dispatch(setUserAccount(null));
      }

      messageApi.open({ type: "error", content: message });
    }
  };

  const loadSelectedSchedule = async (id) => {
    form.resetFields();
    messageApi.open({ type: "loading", content: "Cargando..." });

    try {
      const response = await SchedulesAPI.getSchedules(token, { id });
      const scheduleResponse = response.data;

      if (!scheduleResponse.success) {
        messageApi.destroy();
        messageApi.open({ type: "error", content: scheduleResponse.message });
        return;
      }

      if (scheduleResponse.schedules.length < 1) {
        messageApi.destroy();
        messageApi.open({ type: "error", content: "No encontrado" });
        return;
      }

      const { Id, Description, StartTime, EndTime, WeekDays } =
        scheduleResponse.schedules[0];

      form.setFieldsValue({
        id: Id,
        description: Description,
        startTime: dayjs(StartTime, "HH:mm:ss"),
        endTime: dayjs(EndTime, "HH:mm:ss"),
        weekDays: WeekDays.split(",").map((day) => parseInt(day)),
      });
      messageApi.destroy();
    } catch (err) {
      console.error(err.message);

      let message = "Servicio no disponible, intente más tarde. ";

      if (err.code === "ERR_NETWORK") {
        message = err.response.data.message;

        if (err.response.status === 401) dispatch(setUserAccount(null));
      }

      messageApi.open({ type: "error", content: message });
    }
  };

  const onFinishScheduleForm = async (values) => {
    const { id, description, weekDays } = values;
    const startTime = dayjs(values.startTime).format("HH:mm:ss");
    const endTime = dayjs(values.endTime).format("HH:mm:ss");

    if (weekDays.length < 1) {
      messageApi.open({
        type: "warning",
        content: "Favor elegir al menos un día.",
      });
      return;
    }

    const weekDaysValues = weekDays.join(",");

    try {
      let response = null;

      if (!id || isNaN(id)) {
        response = await SchedulesAPI.createSchedule(token, {
          description,
          startTime,
          endTime,
          weekDays: weekDaysValues,
        });
      } else {
        response = await SchedulesAPI.updateSchedule(token, {
          id,
          description,
          startTime,
          endTime,
          weekDays: weekDaysValues,
        });
      }

      const responseBody = response.data;

      form.resetFields();
      messageApi.open({
        type: responseBody.success ? "success" : "error",
        content: responseBody.message,
      });
      loadSchedulesList();
    } catch (err) {
      console.error(err.message);

      let message = "Servicio no disponible, intente más tarde. ";

      if (err.code === "ERR_NETWORK") {
        message = err.response.data.message;

        if (err.response.status === 401) dispatch(setUserAccount(null));
      }

      messageApi.open({ type: "error", content: message });
    }
  };

  const onDeleteSchedule = async (id) => {
    try {
      const response = await SchedulesAPI.deleteSchedule(token, id);
      const responseBody = response.data;

      form.resetFields();
      messageApi.open({
        type: responseBody.success ? "success" : "error",
        content: responseBody.message,
      });
      loadSchedulesList();
    } catch (err) {
      console.error(err.message);

      let message = "Servicio no disponible, intente más tarde. ";

      if (err.code === "ERR_NETWORK") {
        message = err.response.data.message;

        if (err.response.status === 401) dispatch(setUserAccount(null));
      }

      messageApi.open({ type: "error", content: message });
    }
  };

  useEffect(() => {
    dispatch(setMenu("schedulesMaintenance"));
    loadInitialData();
  }, [dispatch, loadInitialData]);

  return (
    <div className="schedules-maintenance">
      {messageContextHolder}
      <Modal
        title="Confirmación de borrado"
        open={isModalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => {
          onDeleteSchedule(selectedId);
          setSelectedId("");
          setModalOpen(false);
        }}
      >
        <p>¿Desea continuar?</p>
      </Modal>
      <div className="schedules-screen-column">
        <h1>Formulario</h1>
        <Form
          form={form}
          initialValues={schedulesFormInitialValues}
          onFinish={onFinishScheduleForm}
        >
          <Form.Item type="text" name="id" style={{ display: "none" }}>
            <Input maxLength={11} />
          </Form.Item>
          <Form.Item label="Descripción" name="description" required>
            <Input type="text" maxLength={100} required />
          </Form.Item>
          <Form.Item label="Hora inicio (24hrs)" name="startTime" required>
            <TimePicker format="HH:mm:ss" required />
          </Form.Item>
          <Form.Item label="Hora fin (24hrs)" name="endTime" required>
            <TimePicker required />
          </Form.Item>
          <Form.Item label="Días de la semana" name="weekDays" required>
            <Checkbox.Group>
              {WEEKDAYS_REF.map((weekDay) => (
                <Checkbox key={weekDay.value} value={weekDay.value}>
                  {weekDay.label}
                </Checkbox>
              ))}
            </Checkbox.Group>
          </Form.Item>
          <FormButtonsWrap>
            <Button type="primary" htmlType="submit">
              Guardar
            </Button>
            <Button
              onClick={() => {
                form.resetFields();
              }}
            >
              Limpiar
            </Button>
          </FormButtonsWrap>
        </Form>
      </div>
      <div className="schedules-screen-column">
        <h1>Rangos de Horario</h1>
        <DataTotalDisplay
          currentCount={schedulesData.schedules.length}
          totalCount={schedulesData.totalCount}
        />
        <Table
          columns={schedulesTableColumns}
          dataSource={schedulesData.schedules}
          pagination={{
            current: currentPage,
            pageSize: currentPageSize,
            total: schedulesData.totalCount,
          }}
          onChange={async (e) => {
            await loadSchedulesList(e.current, e.pageSize);
            setCurrentPage(e.current);
            setCurrentPageSize(e.pageSize);
          }}
        />
      </div>
    </div>
  );
};

export default SchedulesMaintenancePage;
